import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { forwardRef, useRef, useEffect, useState } from "react"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"
import { Button } from "@/components/ui/button"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

export const ContactSection = forwardRef<HTMLElement>(
  function ContactSection(_, ref) {
    return (
      <section ref={ref} data-section="contact" className="flex min-h-screen flex-col justify-center py-32">
        <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
          <ContactContent />
        </div>
      </section>
    )
  },
)

function ContactContent() {
  const [formData, setFormData]         = useState({ name: "", email: "", message: "" })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted]       = useState(false)
  const [error, setError]               = useState<string | null>(null)

  const containerRef  = useRef<HTMLDivElement>(null)
  const prevActiveRef = useRef<boolean | null>(null)

  const isActive = useSectionScrollStore(
    (s) => s.sections[s.activeSectionIndex]?.type === "contact",
  )

  useGSAP(
    () => {
      const el = containerRef.current
      if (!el) return
      gsap.set(el, { opacity: 0, y: 20 })
    },
    { scope: containerRef },
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    if (prevActiveRef.current === isActive) return

    const wasActive = prevActiveRef.current
    prevActiveRef.current = isActive

    gsap.killTweensOf(el)

    if (isActive) {
      gsap.to(el, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" })
    } else if (wasActive !== null) {
      gsap.to(el, { opacity: 0, y: -10, duration: 0.2, ease: "power2.in" })
    }
  }, [isActive])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })
      if (!res.ok) throw new Error("Failed to send")
      setSubmitted(true)
    } catch {
      setError("Something went wrong. Please try again or email me directly.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <div ref={containerRef} data-contact-content className="pointer-events-auto w-full">
      {/*
        2×2 grid on md+: text in area [row1,col1], form in area [row2,col2].
        Areas [row1,col2] and [row2,col1] are intentionally empty.
        Mobile: stacked column.
      */}
      <div className="flex flex-col gap-12 md:grid md:grid-cols-2 md:gap-x-16 md:gap-y-0">

        {/* Area 1 (row 1, col 1) — info */}
        <div data-contact-info className="flex flex-col gap-6 md:col-start-1 md:row-start-1">
          <div>
            <h2 className="mb-3 text-3xl font-black tracking-tight text-foreground md:text-5xl">Get in Touch</h2>
            <p className="text-base leading-relaxed text-foreground/55 md:text-lg">
              Have a project in mind? Let&apos;s create something amazing together.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/30">Email</span>
            <a
              href="mailto:hello@brian-clark.com"
              className="self-start text-base text-foreground/65 transition-colors hover:text-accent md:text-lg"
            >
              hello@brian-clark.com
            </a>
          </div>
        </div>

        {/* Area 4 (row 2, col 2) — form */}
        <div data-contact-form className="md:col-start-2 md:row-start-2">
          {submitted ? (
            <div data-contact-success className="rounded-lg border border-white/10 bg-white/5 p-8">
              <h3 className="mb-2 text-xl font-semibold text-foreground">Message Sent!</h3>
              <p className="text-sm text-foreground/60">
                Thanks for reaching out. I&apos;ll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="contact-name" className="text-base md:text-lg">Name</FieldLabel>
                    <Input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="h-10"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="contact-email" className="text-base md:text-lg">Email</FieldLabel>
                    <Input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="h-10"
                    />
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="contact-message" className="text-base md:text-lg">Message</FieldLabel>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={8}
                    placeholder="Tell me about your project..."
                    className="resize-none min-h-32"
                  />
                </Field>
                {error && <p className="text-sm text-red-400">{error}</p>}
                <Button type="submit" disabled={isSubmitting} className="h-10 self-start">
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </FieldGroup>
            </form>
          )}
        </div>

      </div>
    </div>
  )
}
